import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Plane,
  Clock,
  MapPin,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ArrowRight,
  Navigation,
  Timer,
  Calendar,
  Info
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_STYLES = {
  scheduled: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
  active: { bg: 'bg-green-100', text: 'text-green-700', icon: Plane },
  in_flight: { bg: 'bg-green-100', text: 'text-green-700', icon: Plane },
  landed: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle2 },
  on_time: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2 },
  delayed: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Timer },
  cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle },
  diverted: { bg: 'bg-orange-100', text: 'text-orange-700', icon: Navigation },
  unknown: { bg: 'bg-gray-100', text: 'text-gray-700', icon: Info }
};

const StatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.unknown;
  const Icon = style.icon;
  
  return (
    <Badge className={`${style.bg} ${style.text} px-3 py-1 text-sm font-medium`}>
      <Icon className="h-3 w-3 mr-1" />
      {status.replace('_', ' ').toUpperCase()}
    </Badge>
  );
};

const formatTime = (dateStr) => {
  if (!dateStr) return '--:--';
  try {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '--:--';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: '2-digit', 
      month: 'short' 
    });
  } catch {
    return '';
  }
};

const FlightSegmentStatus = ({ segment, isReturn = false }) => {
  if (!segment?.success || !segment?.flight) {
    return (
      <Card className="border-0 shadow-sm bg-gray-50">
        <CardContent className="p-4 text-center text-gray-500">
          <AlertCircle className="h-5 w-5 mx-auto mb-2" />
          Unable to retrieve flight status
        </CardContent>
      </Card>
    );
  }

  const { flight } = segment;
  const departure = flight.departure || {};
  const arrival = flight.arrival || {};
  const live = flight.live;

  return (
    <Card className="border-0 shadow-md overflow-hidden" data-testid="flight-segment-status">
      {/* Header with flight info and status */}
      <div className={`px-4 py-3 ${isReturn ? 'bg-gradient-to-r from-purple-600 to-purple-700' : 'bg-gradient-to-r from-blue-600 to-blue-700'} text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Plane className={`h-5 w-5 ${isReturn ? 'rotate-180' : ''}`} />
            </div>
            <div>
              <p className="font-bold text-lg">{flight.flight_number}</p>
              <p className="text-sm text-white/80">{flight.airline_name}</p>
            </div>
          </div>
          <StatusBadge status={flight.status} />
        </div>
      </div>

      <CardContent className="p-4">
        {/* Departure and Arrival */}
        <div className="flex items-stretch justify-between mb-4">
          {/* Departure */}
          <div className="text-center flex-1">
            <p className="text-3xl font-bold text-slate-800">
              {formatTime(departure.actual || departure.scheduled)}
            </p>
            {departure.actual && departure.scheduled && departure.actual !== departure.scheduled && (
              <p className="text-xs text-slate-400 line-through">
                {formatTime(departure.scheduled)}
              </p>
            )}
            <p className="text-lg font-semibold text-blue-600 mt-1">{departure.airport}</p>
            <p className="text-xs text-slate-500">{departure.city}</p>
            <p className="text-xs text-slate-400 mt-1">{formatDate(departure.scheduled)}</p>
            {departure.terminal && (
              <p className="text-xs text-slate-500 mt-1">
                Terminal {departure.terminal} {departure.gate && `• Gate ${departure.gate}`}
              </p>
            )}
            {departure.delay_minutes > 0 && (
              <Badge className="mt-2 bg-amber-100 text-amber-700 text-xs">
                +{departure.delay_minutes} min delay
              </Badge>
            )}
          </div>

          {/* Flight Path */}
          <div className="flex-1 flex flex-col items-center justify-center px-4">
            <div className="w-full h-0.5 bg-gradient-to-r from-blue-500 via-slate-300 to-emerald-500 relative">
              <Plane className="h-4 w-4 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white" />
            </div>
            {flight.aircraft && (
              <p className="text-xs text-slate-400 mt-2">{flight.aircraft}</p>
            )}
          </div>

          {/* Arrival */}
          <div className="text-center flex-1">
            <p className="text-3xl font-bold text-slate-800">
              {formatTime(arrival.actual || arrival.estimated || arrival.scheduled)}
            </p>
            {(arrival.actual || arrival.estimated) && arrival.scheduled && 
             (arrival.actual || arrival.estimated) !== arrival.scheduled && (
              <p className="text-xs text-slate-400 line-through">
                {formatTime(arrival.scheduled)}
              </p>
            )}
            <p className="text-lg font-semibold text-emerald-600 mt-1">{arrival.airport}</p>
            <p className="text-xs text-slate-500">{arrival.city}</p>
            <p className="text-xs text-slate-400 mt-1">{formatDate(arrival.scheduled)}</p>
            {arrival.terminal && (
              <p className="text-xs text-slate-500 mt-1">
                Terminal {arrival.terminal} {arrival.gate && `• Gate ${arrival.gate}`}
              </p>
            )}
            {arrival.delay_minutes > 0 && (
              <Badge className="mt-2 bg-amber-100 text-amber-700 text-xs">
                +{arrival.delay_minutes} min delay
              </Badge>
            )}
          </div>
        </div>

        {/* Live Tracking Data */}
        {live && (
          <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-green-700">Live Tracking</span>
            </div>
            <div className="grid grid-cols-4 gap-3 text-xs">
              <div>
                <p className="text-slate-500">Altitude</p>
                <p className="font-semibold text-slate-700">{live.altitude?.toLocaleString()} ft</p>
              </div>
              <div>
                <p className="text-slate-500">Speed</p>
                <p className="font-semibold text-slate-700">{live.speed} km/h</p>
              </div>
              <div>
                <p className="text-slate-500">Latitude</p>
                <p className="font-semibold text-slate-700">{live.latitude?.toFixed(2)}°</p>
              </div>
              <div>
                <p className="text-slate-500">Longitude</p>
                <p className="font-semibold text-slate-700">{live.longitude?.toFixed(2)}°</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const FlightStatusTracker = ({ pnr, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusData, setStatusData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${BACKEND_URL}/api/booking-status/${pnr}`);
      
      if (response.data.success) {
        setStatusData(response.data);
        setLastRefresh(new Date());
      } else {
        setError(response.data.error || 'Failed to retrieve flight status');
      }
    } catch (err) {
      console.error('Flight status error:', err);
      setError('Unable to retrieve flight status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pnr) {
      fetchStatus();
    }
  }, [pnr]);

  // Auto-refresh every 5 minutes if enabled
  useEffect(() => {
    let interval;
    if (autoRefresh && pnr) {
      interval = setInterval(fetchStatus, 5 * 60 * 1000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, pnr]);

  // Separate outbound and return segments
  const outboundSegments = statusData?.segments?.filter(
    s => !s?.flight?.segment_info?.is_return
  ) || [];
  const returnSegments = statusData?.segments?.filter(
    s => s?.flight?.segment_info?.is_return
  ) || [];

  return (
    <div className="space-y-4" data-testid="flight-status-tracker">
      {/* Header */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <Plane className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-xl">Flight Status</CardTitle>
                <p className="text-slate-300 text-sm">Booking: {pnr}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {statusData && (
                <StatusBadge status={statusData.overall_status} />
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchStatus}
                disabled={loading}
                className="gap-2"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Refresh
              </Button>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Auto-refresh (5 min)
              </label>
            </div>
            {lastRefresh && (
              <p className="text-xs text-slate-500">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && !statusData && (
        <Card className="border-0 shadow-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500 mb-4" />
            <p className="text-slate-600">Retrieving flight status...</p>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-0 shadow-md border-l-4 border-l-red-500">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Flight Segments */}
      {statusData && !loading && (
        <div className="space-y-6">
          {/* Outbound Flights */}
          {outboundSegments.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <ArrowRight className="h-4 w-4" />
                Outbound Flight{outboundSegments.length > 1 ? 's' : ''}
              </h3>
              <div className="space-y-3">
                {outboundSegments.map((segment, idx) => (
                  <FlightSegmentStatus key={idx} segment={segment} isReturn={false} />
                ))}
              </div>
            </div>
          )}

          {/* Return Flights */}
          {returnSegments.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <ArrowRight className="h-4 w-4 rotate-180" />
                Return Flight{returnSegments.length > 1 ? 's' : ''}
              </h3>
              <div className="space-y-3">
                {returnSegments.map((segment, idx) => (
                  <FlightSegmentStatus key={idx} segment={segment} isReturn={true} />
                ))}
              </div>
            </div>
          )}

          {/* Info Notice */}
          <Card className="border-0 bg-blue-50 shadow-sm">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">About Flight Status</p>
                <p className="text-blue-600">
                  Flight status is updated in real-time when available. For the most accurate 
                  information, please also check with your airline directly or visit the airport 
                  departure screens.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FlightStatusTracker;
